import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Heading,
  Main,
  SectionLeft,
  SectionRight,
  SuggestionsList,
  SuggestionItem,
  SearchContainer,
  ErrorMessage,
  Wrapper,
  SubLabel,
  CountryCodeSelect,
  IconWrapper,
  PhoneInputContainer,
  SelectWrapper,
} from './addressSearch.style';
import { Button, InputField } from '../../../components/uielements';
import useDebounce from '../../../hooks/useDebounce';
import { getAddressesList } from '../../../services/getAddressList';
import { ChevronDown } from 'lucide-react';

import backgroundImg from '../../../assets/PNG/address-search-page.png';
import backgroundImgCompress from '../../../assets/PNG/address-search-page-compress.png';
import useImageLoader from '../../../hooks/useImageLoader';
import { IntlMessages } from '../../../helper/langHelper';
import { countryCodes, EMAIL_REGEX } from '../../../settings/constants';
import { useLanguage } from '../../../hooks/useLanguageSwitch';
import { getRecentApplicantInfo } from '../../../services/application';
import Cache from '../../../helper/globalCacheHelper';
import { clearItem } from '../../../helper/localStorageHelper';

// Cache constants for recent applicant info
const RECENT_APPLICANT_CACHE_ACTION_NAME = 'RECENT_APPLICANT_INFO';
const RECENT_APPLICANT_CACHE_KEY = 'recent_applicant_data';

const AddressSearch = () => {
  const [formState, setFormState] = useState({
    fullName: '',
    email: '',
    phone: '',
    countryCode: '+61',
    addressInfo: null,
    clickedAddress: '',
    isAddressFind: false,
    isSuggestionClicked: false,
    activeIndex: -1,
    errors: {
      email: '',
      phone: '',
    },
  });

  const [isLoadingRecentInfo, setIsLoadingRecentInfo] = useState(false);

  const navigate = useNavigate();
  const addressSearchBg = useImageLoader(backgroundImgCompress, backgroundImg);

  const [searchParams] = useSearchParams();
  const flow = searchParams.get('flow');

  const {
    searchTerm,
    setSearchTerm,
    results: suggestions,
    isLoading,
    clearResults,
    isAPICalled,
  } = useDebounce(getAddressesList, 300);

  // Function to fetch recent applicant info with caching
  const fetchRecentApplicantInfo = async (forceRefresh = false) => {
    if (
      !forceRefresh &&
      Cache.hasCache(RECENT_APPLICANT_CACHE_ACTION_NAME, RECENT_APPLICANT_CACHE_KEY)
    ) {
      const cachedData = Cache.getCache(
        RECENT_APPLICANT_CACHE_ACTION_NAME,
        RECENT_APPLICANT_CACHE_KEY
      );

      return cachedData;
    }

    try {
      setIsLoadingRecentInfo(true);
      const response = await getRecentApplicantInfo();

      Cache.setCache(RECENT_APPLICANT_CACHE_ACTION_NAME, RECENT_APPLICANT_CACHE_KEY, response);

      setIsLoadingRecentInfo(false);
      return response;
    } catch (error) {
      setIsLoadingRecentInfo(false);
      console.error('Error fetching recent applicant info:', error);
      return null;
    }
  };

  // Auto-fill form inputs based on recent applicant info
  const autoFillFormInputs = (applicantData) => {
    if (!applicantData) return;

    setFormState((prev) => ({
      ...prev,
      fullName: applicantData.fullName || applicantData.name || '',
      email: applicantData.email || '',
      phone: applicantData.phoneNumber ? applicantData.phoneNumber.replace(/^\+\d{1,2}/, '') : '', // Remove country code if present
      countryCode:
        applicantData.countryCode || extractCountryCode(applicantData.phoneNumber) || '+61',
      errors: {
        email: '',
        phone: '',
      },
    }));
  };

  // Helper function to extract country code from phone number
  const extractCountryCode = (phoneNumber) => {
    if (!phoneNumber) return '+61';

    const countryCodeMatch = phoneNumber.match(/^(\+\d{1,2})/);
    return countryCodeMatch ? countryCodeMatch[1] : '+61';
  };

  // Load recent applicant info on component mount
  useEffect(() => {
    const loadRecentApplicantInfo = async () => {
      const applicantData = await fetchRecentApplicantInfo();

      if (applicantData) {
        autoFillFormInputs(applicantData);
      }
    };

    loadRecentApplicantInfo();
  }, []);

  // Generic input handler
  const handleInputChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
      errors: {
        ...prev.errors,
        [field]: getFieldError(field, value),
      },
    }));
  };

  // Field-specific error validation
  const getFieldError = (field, value) => {
    switch (field) {
      case 'email':
        if (!value.trim()) return '';
        return EMAIL_REGEX.test(value) ? '' : <IntlMessages id="validation.email.invalid" />;
      case 'phone': {
        if (!value.trim()) return '';
        const phoneRegex = /^\d{9}$/;
        return phoneRegex.test(value.replace(/\D/g, '')) ? (
          ''
        ) : (
          <IntlMessages id="validation.phone.invalid" />
        );
      }
      default:
        return '';
    }
  };

  // Address search input handler
  const handleAddressInputChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setFormState((prev) => ({
      ...prev,
      isSuggestionClicked: false,
      activeIndex: -1,
      clickedAddress: '',
      isAddressFind: false,
      addressInfo: null,
    }));
  };

  // Country code change handler
  const handleCountryCodeChange = (event) => {
    setFormState((prev) => ({
      ...prev,
      countryCode: event.target.value,
    }));
  };

  // Suggestion click handler
  const handleSuggestionClick = (selectedAddress) => {
    setFormState((prev) => ({
      ...prev,
      addressInfo: selectedAddress,
      clickedAddress: selectedAddress?.address || '',
      isAddressFind: true,
      isSuggestionClicked: true,
    }));
    clearResults();
  };

  // Keyboard navigation for suggestions
  const handleKeyDown = (event) => {
    if (!suggestions.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setFormState((prev) => ({
        ...prev,
        activeIndex:
          prev.activeIndex < suggestions.length - 1 ? prev.activeIndex + 1 : prev.activeIndex,
      }));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setFormState((prev) => ({
        ...prev,
        activeIndex: prev.activeIndex > 0 ? prev.activeIndex - 1 : prev.activeIndex,
      }));
    } else if (event.key === 'Enter' && formState.activeIndex >= 0) {
      event.preventDefault();
      handleSuggestionClick(suggestions[formState.activeIndex]);
    } else if (event.key === 'Escape') {
      clearResults();
      setFormState((prev) => ({ ...prev, activeIndex: -1 }));
    }
  };

  // Form submission handler
  const handleSubmit = () => {
    const { fullName, email, phone, addressInfo } = formState;
    clearItem('screenshotId');

    // navigate('/mapView', {
    //   state: {
    //     address: addressInfo,
    //     fullName,
    //     email,
    //     phone: `${formState.countryCode}${phone}`,
    //   },
    // });

    navigate(`/promptSelect${flow ? `?flow=${flow}` : ''}`, {
      state: {
        searchAddress: addressInfo,
        fullName,
        phone: `${formState.countryCode}${phone}`,
        email,
      },
    });
    // navigate(`/mapView${flow ? `?flow=${flow}` : ''}`, {
    //   state: {
    //     address: addressInfo,
    //     fullName,
    //     email,
    //     phone: `${formState.countryCode}${phone}`,
    //   },
    // });
  };

  // Form validation
  const isFormValid = () => {
    const { fullName, email, phone, isSuggestionClicked, errors } = formState;
    return (
      fullName.trim() &&
      email.trim() &&
      EMAIL_REGEX.test(email) &&
      phone.trim() &&
      !errors.email &&
      !errors.phone &&
      isSuggestionClicked
    );
  };

  const lang = useLanguage();
  const selectedLanguage = lang?.selectedLang;

  return (
    <Main role="main">
      <SectionLeft>
        <Heading role="heading" aria-level="1">
          <IntlMessages id="address.search.header" />
        </Heading>
        <Wrapper>
          {/* Full Name Input */}
          <SubLabel role="heading" aria-level="2">
            <IntlMessages id="address.search.applicant.name" />
          </SubLabel>

          <IntlMessages id="input.fullname.placeholder" defaultMessage="Enter your full name">
            {(placeholder) => (
              <InputField
                type="text"
                placeholder={placeholder}
                value={formState.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                autoComplete="name"
                aria-label="Enter your full name"
                aria-required="true"
                lang={selectedLanguage}
                disabled={isLoadingRecentInfo}
              />
            )}
          </IntlMessages>

          {/* Email Input */}
          <SubLabel role="heading" aria-level="2">
            <IntlMessages id="input.email.lebel" />
          </SubLabel>

          <IntlMessages id="input.email.placeholder" defaultMessage="Enter your email address">
            {(placeholder) => (
              <InputField
                type="email"
                placeholder={placeholder}
                value={formState.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                autoComplete="email"
                aria-label="Enter your email address"
                aria-required="true"
                aria-invalid={!!formState.errors.email}
                aria-describedby={formState.errors.email ? 'email-error' : undefined}
                lang={selectedLanguage}
                disabled={isLoadingRecentInfo}
              />
            )}
          </IntlMessages>
          {formState.errors.email && (
            <ErrorMessage id="email-error" role="alert" aria-live="assertive">
              <span aria-hidden="true">⚠️</span> {formState.errors.email}
            </ErrorMessage>
          )}

          {/* Phone Number Input */}
          <SubLabel role="heading" aria-level="2">
            <IntlMessages id="input.phone.label" />
          </SubLabel>
          <PhoneInputContainer>
            <SelectWrapper>
              <CountryCodeSelect
                id="country-code"
                value={formState.countryCode}
                onChange={handleCountryCodeChange}
                aria-label="Select country code"
                disabled={isLoadingRecentInfo}
              >
                {countryCodes.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.code} {country.country}
                  </option>
                ))}
              </CountryCodeSelect>
              <IconWrapper>
                <ChevronDown size={16} color="#606266" />
              </IconWrapper>
            </SelectWrapper>
            <IntlMessages id="input.phone.placeholder" defaultMessage="Enter your phone number">
              {(placeholder) => (
                <InputField
                  type="tel"
                  id="phone"
                  placeholder={placeholder}
                  name="phone"
                  value={formState.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  aria-required="true"
                  aria-invalid={!!formState.errors.phone}
                  aria-describedby={formState.errors.phone ? 'phone-error' : undefined}
                  maxLength={9}
                  lang={selectedLanguage}
                  disabled={isLoadingRecentInfo}
                />
              )}
            </IntlMessages>
          </PhoneInputContainer>
          {formState.errors.phone && (
            <ErrorMessage id="phone-error" role="alert" aria-live="assertive">
              <span aria-hidden="true">⚠️</span> {formState.errors.phone}
            </ErrorMessage>
          )}

          {/* Address Search Input */}
          <SubLabel role="heading" aria-level="2">
            <IntlMessages id="address.search.Qns" />
          </SubLabel>
          <SearchContainer role="search">
            <IntlMessages id="input.search.placeholder" defaultMessage="Enter your address here">
              {(placeholder) => (
                <InputField
                  type="text"
                  placeholder={placeholder}
                  value={formState.clickedAddress || searchTerm}
                  onChange={handleAddressInputChange}
                  onKeyDown={handleKeyDown}
                  autoComplete="street-address"
                  aria-label="Search for your address"
                  aria-describedby="address-hint"
                  aria-expanded={suggestions.length > 0}
                  aria-controls="address-suggestions"
                  aria-activedescendant={
                    formState.activeIndex >= 0 ? `suggestion-${formState.activeIndex}` : undefined
                  }
                />
              )}
            </IntlMessages>

            <span id="address-hint" className="visually-hidden">
              Start typing to search for your address
            </span>
            <SuggestionsList
              id="address-suggestions"
              role="listbox"
              aria-label="Address suggestions"
            >
              {isLoading ? (
                <SuggestionItem role="status" aria-live="polite">
                  <IntlMessages id="loading.suggestions" defaultMessage="Loading suggestions..." />
                </SuggestionItem>
              ) : (
                suggestions.map((suggestion, index) => (
                  <SuggestionItem
                    key={`suggestion-${index}`}
                    id={`suggestion-${index}`}
                    role="option"
                    aria-selected={index === formState.activeIndex}
                    onClick={() => handleSuggestionClick(suggestion)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleSuggestionClick(suggestion);
                      }
                    }}
                    tabIndex={0}
                  >
                    {suggestion?.address}
                  </SuggestionItem>
                ))
              )}
            </SuggestionsList>
          </SearchContainer>
        </Wrapper>

        {/* Loading indicator for recent applicant info */}
        {isLoadingRecentInfo && (
          <div role="status" aria-live="polite">
            <IntlMessages
              id="loading.recent.applicant"
              defaultMessage="Loading recent applicant information..."
            />
          </div>
        )}

        {isAPICalled && !formState.isAddressFind && (
          <ErrorMessage role="alert" aria-live="assertive">
            <span aria-hidden="true">⚠️</span> <IntlMessages id="address.unsupported.message" />
          </ErrorMessage>
        )}

        <Button onClick={handleSubmit} disabled={!isFormValid()} aria-label="Proceed to map view">
          <IntlMessages id="button.label" />
        </Button>
      </SectionLeft>
      <SectionRight aria-hidden="true" bg={addressSearchBg} />
    </Main>
  );
};

export default AddressSearch;
